import React, { useCallback, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { Search } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../UI/card';
import { Button } from '../UI/button';
import { Input } from '../UI/input';
import { Badge } from '../UI/badge';
import LoadingSpinner from '../UI/LoadingSpinner';
import { useLocale } from '../../contexts/LocaleContext';
import { themeClasses } from '../../config/themeClasses';
import { tenantCompanyService } from '../../services/tenantCompanyService';
import { companyService } from '../../services/companyService';
import { userCompaniesService } from '../../services/userCompaniesService';
import {
  getTenantSupplierCountry,
  getTenantSupplierCriticality,
  getTenantSupplierId,
  getTenantSupplierLegalName,
  getTenantSupplierName,
  getTenantSupplierTier,
} from '../../utils/tenantSupplierUtils';
import { cn } from '../../lib/utils';

function TenantSuppliersPanel({
  suppliers = [],
  loading = false,
  saving = false,
  onSuppliersChange,
}) {
  const { t } = useLocale();
  const [companyId, setCompanyId] = useState('');
  const [notes, setNotes] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [companyOptions, setCompanyOptions] = useState([]);
  const [loadingCompanies, setLoadingCompanies] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);

  const loadCompanies = useCallback(async () => {
    setLoadingCompanies(true);
    const [userResponse, globalResponse] = await Promise.all([
      userCompaniesService.listUserCompanies(),
      companyService.getAllCompanies(),
    ]);
    const byTicker = new Map();
    const addCompanies = (list) => {
      (list || []).forEach((company) => {
        const ticker = String(company.ticker || '').trim().toUpperCase();
        if (ticker) {
          byTicker.set(ticker, company);
        }
      });
    };
    if (userResponse.success) {
      addCompanies(userResponse.data);
    }
    if (globalResponse.success) {
      addCompanies(globalResponse.data);
    }
    setCompanyOptions(Array.from(byTicker.values()));
    setLoadingCompanies(false);
  }, []);

  useEffect(() => {
    loadCompanies();
  }, [loadCompanies]);

  const filteredCompanies = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return [];
    return companyOptions
      .filter(
        (company) =>
          String(company.name || '')
            .toLowerCase()
            .includes(term) ||
          String(company.ticker || '')
            .toLowerCase()
            .includes(term)
      )
      .slice(0, 12);
  }, [companyOptions, searchTerm]);

  const handleSelectCompany = (company) => {
    const ticker = String(company.ticker || '').trim().toUpperCase();
    setCompanyId(ticker);
    setSearchTerm(company.name ? `${company.name} (${ticker})` : ticker);
    setShowSuggestions(false);
  };

  const handleAdd = async (event) => {
    event.preventDefault();
    const trimmedId = companyId.trim().toUpperCase();
    if (!trimmedId) {
      return;
    }

    const response = await tenantCompanyService.addTenantSupplier(undefined, {
      company_id: trimmedId,
      notes: notes.trim(),
    });

    if (response.success) {
      toast.success(t('tenantSuppliersAddSuccess'));
      setCompanyId('');
      setNotes('');
      setSearchTerm('');
      onSuppliersChange?.();
    } else {
      toast.error(response.message || t('tenantSuppliersAddFailed'));
    }
  };

  const handleRemove = async (ticker) => {
    const response = await tenantCompanyService.removeTenantSupplier(undefined, ticker);
    if (response.success) {
      toast.success(t('tenantSuppliersRemoveSuccess'));
      onSuppliersChange?.();
    } else {
      toast.error(response.message || t('tenantSuppliersRemoveFailed'));
    }
  };

  return (
    <Card className={themeClasses.appKit.sectionShell}>
      <CardHeader>
        <CardTitle>{t('tenantSuppliersTitle')}</CardTitle>
        <CardDescription>{t('tenantSuppliersDescription')}</CardDescription>
        <p className="text-sm text-text-secondary">{t('tenantSuppliersTickerHint')}</p>
      </CardHeader>
      <CardContent className="flex flex-col gap-6">
        <form onSubmit={handleAdd} className="flex flex-col gap-4">
          <div className="flex flex-col gap-4 sm:flex-row sm:flex-wrap sm:items-end">
            <div className="flex min-w-[12rem] flex-1 flex-col gap-2">
              <label htmlFor="tenant-supplier-search" className="text-sm font-medium text-text-primary">
                {t('tenantSuppliersCompanyId')}
              </label>
              <div className="relative">
                <Search
                  className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-text-tertiary"
                  aria-hidden
                />
                <Input
                  id="tenant-supplier-search"
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setCompanyId(e.target.value.trim().toUpperCase().slice(0, 32));
                    setShowSuggestions(true);
                  }}
                  onFocus={() => setShowSuggestions(true)}
                  onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
                  placeholder={t('tenantSuppliersSearchPlaceholder')}
                  disabled={saving}
                  className="pl-10"
                  autoComplete="off"
                />
                {showSuggestions && searchTerm.trim() ? (
                  <ul
                    className="absolute z-20 mt-1 max-h-48 w-full overflow-y-auto rounded-lg border border-border-light bg-background-card shadow-md"
                    role="listbox"
                  >
                    {loadingCompanies ? (
                      <li className="flex justify-center p-3">
                        <LoadingSpinner size="sm" />
                      </li>
                    ) : filteredCompanies.length === 0 ? (
                      <li className="p-3 text-sm text-text-secondary">
                        {t('noSuppliersFound')}
                        {companyId.trim() ? (
                          <span className="mt-1 block text-text-tertiary">
                            {t('tenantSuppliersCompanyId')}: {companyId.trim().toUpperCase()}
                          </span>
                        ) : null}
                      </li>
                    ) : (
                      filteredCompanies.map((company) => (
                        <li key={company.ticker}>
                          <button
                            type="button"
                            className={cn(
                              'flex w-full flex-col items-start gap-0.5 px-3 py-2 text-left text-sm hover:bg-background-primary',
                              companyId === company.ticker && 'bg-primary-50'
                            )}
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => handleSelectCompany(company)}
                          >
                            <span className="font-medium text-text-primary">{company.name}</span>
                            <span className="text-text-tertiary">{company.ticker}</span>
                          </button>
                        </li>
                      ))
                    )}
                  </ul>
                ) : null}
              </div>
            </div>
            <div className="flex min-w-[12rem] flex-1 flex-col gap-2">
              <label htmlFor="tenant-supplier-notes" className="text-sm font-medium text-text-primary">
                {t('tenantSuppliersNotes')}
              </label>
              <Input
                id="tenant-supplier-notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                disabled={saving}
              />
            </div>
            <Button type="submit" disabled={saving || !companyId.trim()}>
              {t('tenantSuppliersAdd')}
            </Button>
          </div>
        </form>

        {loading ? (
          <div className="flex justify-center py-8">
            <LoadingSpinner />
          </div>
        ) : suppliers.length === 0 ? (
          <p className="text-sm text-text-secondary">{t('tenantSuppliersEmpty')}</p>
        ) : (
          <ul className="divide-y divide-border-light rounded-lg border border-border-light">
            {suppliers.map((item) => {
              const id = getTenantSupplierId(item);
              const name = getTenantSupplierName(item);
              const tier = getTenantSupplierTier(item);
              const legalName = getTenantSupplierLegalName(item);
              const country = getTenantSupplierCountry(item);
              const criticality = getTenantSupplierCriticality(item);
              return (
                <li
                  key={id}
                  className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 text-sm"
                >
                  <div className="min-w-0">
                    <p className="font-medium text-text-primary">{name}</p>
                    <p className="text-text-tertiary">{id}</p>
                    {legalName && legalName !== name ? (
                      <p className="text-text-secondary">
                        {t('tenantSuppliersLegalName')}: {legalName}
                      </p>
                    ) : null}
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    {tier ? <Badge variant="secondary">{tier}</Badge> : null}
                    {country ? <Badge variant="outline">{country}</Badge> : null}
                    {criticality != null && !Number.isNaN(criticality) ? (
                      <Badge variant="outline">
                        {t('tenantSuppliersCriticality')}: {criticality}
                      </Badge>
                    ) : null}
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={saving}
                      onClick={() => handleRemove(id)}
                    >
                      {t('tenantSuppliersRemove')}
                    </Button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

export default TenantSuppliersPanel;
